import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SearchResults, CountyResearchRecord } from "./types";
import { RecordDetailModal } from "./RecordDetailModal";
import { formatCurrency } from "@/components/investor/utils";
import { format } from "date-fns";
import { ExternalLink, Eye, RefreshCw, Trash2, Download, ChevronLeft, ChevronRight, ChevronDown, Save, X, Upload, Camera, Pencil, Copy, Search, Mail, Loader2, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { NameCleanupWizard, type CleanedRecord } from './NameCleanupWizard';

interface ResearchResultsProps {
  searchResults: SearchResults;
  onRefresh: () => void;
  defaultCountyFilter?: string;
}

// Texas counties use "Lender/Borrower" terminology instead of "Seller/Buyer"
const TEXAS_COUNTIES = [
  'bandera', 'bell', 'blanco', 'brazos', 'brewster', 'collin', 'comal', 'coryell',
  'dallas', 'gillespie', 'goliad', 'guadalupe', 'hidalgo', 'hockley', 'jimhogg',
  'kendall', 'kerr', 'kleberg', 'midland', 'nueces', 'potter', 'randall', 'reagan',
  'reeves', 'sanpatricio', 'travis', 'williamson', 'zapata'
];

const DOT_COUNTIES = ['collin', 'coryell', 'brewster', 'whatcom', 'comal', 'dallas', 'gillespie', 'sherman', 'fannin', 'goliad', 'sanjuan', 'midland'];

const isTexasCounty = (county: string) => TEXAS_COUNTIES.includes((county || '').toLowerCase());
const isDotCounty = (county?: string) => DOT_COUNTIES.includes((county || '').toLowerCase());

const getSellerName = (record: CountyResearchRecord) =>
  isDotCounty(record.county) ? (record.grantee_name || '') : (record.grantor_name || '');

const getSellerAddress = (record: CountyResearchRecord) => {
  if (!isDotCounty(record.county)) return record.grantor_address || '';
  return record.grantee_address || '';
};

const getBorrowerName = (record: CountyResearchRecord) =>
  isDotCounty(record.county) ? (record.grantor_name || '') : (record.grantee_name || '');

const getBorrowerAddress = (record: CountyResearchRecord) => {
  if (!isDotCounty(record.county)) return record.grantee_address || '';
  return record.grantor_address || '';
};

export function ResearchResults({ searchResults, defaultCountyFilter, onRefresh }: ResearchResultsProps) {
  const [selectedRecord, setSelectedRecord] = useState<CountyResearchRecord | null>(null);
  const [records, setRecords] = useState<CountyResearchRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const [manualAddress, setManualAddress] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [isSavingToSheets, setIsSavingToSheets] = useState(false);
  const [countyFilter, setCountyFilter] = useState<string>(defaultCountyFilter || "all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [capturingRecordId, setCapturingRecordId] = useState<string | null>(null);
  const [isSendingToMailing, setIsSendingToMailing] = useState(false);
  const [quickEditId, setQuickEditId] = useState<string | null>(null);
  const [quickEditAddress, setQuickEditAddress] = useState('');
  const [quickEditLoanId, setQuickEditLoanId] = useState<string | null>(null);
  const [quickEditLoanAmount, setQuickEditLoanAmount] = useState('');
  const [showNameCleanup, setShowNameCleanup] = useState(false);
  const [nameCleanupRecords, setNameCleanupRecords] = useState<CountyResearchRecord[]>([]);

  // Sync county filter when the parent's selected county changes
  useEffect(() => {
    if (defaultCountyFilter) {
      setCountyFilter(defaultCountyFilter);
    }
  }, [defaultCountyFilter]);
  const [uploadingRecordId, setUploadingRecordId] = useState<string | null>(null);
  const fileInputRef = useState<{ [key: string]: HTMLInputElement | null }>({})[0];
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingRecord, setEditingRecord] = useState<Partial<CountyResearchRecord> & { id: string } | null>(null);

  const allRecords = records.length > 0 ? records : searchResults.records;
  
  let displayRecords = allRecords;
  
  // Apply county filter
  if (countyFilter !== "all") {
    displayRecords = displayRecords.filter(r => r.county?.toLowerCase() === countyFilter.toLowerCase());
  }
  
  // Apply status filter
  if (statusFilter === "melissa") {
    displayRecords = displayRecords.filter(r => r.status === 'ready_for_melissa');
  } else if (statusFilter === "needs_review") {
    displayRecords = displayRecords.filter(r => r.address_needs_review);
  } else if (statusFilter === "has_address") {
    displayRecords = displayRecords.filter(r => getSellerAddress(r).trim() !== '');
  } else if (statusFilter === "no_address") {
    displayRecords = displayRecords.filter(r => getSellerAddress(r).trim() === '');
  } else if (statusFilter === "blacklisted") {
    displayRecords = displayRecords.filter(r => r.status === 'blacklisted');
  }
  
  const recordsNeedingReview = displayRecords.filter(r => r.address_needs_review);
  const comalRecordsNeedingReview = displayRecords.filter(r => (r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '');
  const recordsMissingAddress = displayRecords.filter(r => getSellerAddress(r).trim() === '');
  const recordsReadyForMelissa = allRecords.filter(r => r.status === 'ready_for_melissa');
  const counties = Array.from(new Set(allRecords.map(r => r.county).filter(Boolean)));
  
  // Determine if we're in address-capture review mode (for counties like Warren where address_needs_review isn't set)
  const currentReviewRecord = reviewIndex !== null ? displayRecords[reviewIndex] : null;
  const isAddressCaptureMode = !!currentReviewRecord && !currentReviewRecord.address_needs_review && getSellerAddress(currentReviewRecord).trim() === '';
  const isComalReviewMode = !!currentReviewRecord && (currentReviewRecord.county || '').toLowerCase() === 'comal';
  const activeReviewList = isComalReviewMode ? comalRecordsNeedingReview : isAddressCaptureMode ? recordsMissingAddress : recordsNeedingReview;
  
  // Build review record numbers based on the active review mode
  const reviewRecordNumbers = displayRecords
    .map((r, idx) => {
      if (isComalReviewMode) {
        return ((r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '') ? idx : -1;
      } else if (isAddressCaptureMode) {
        return getSellerAddress(r).trim() === '' ? idx : -1;
      } else {
        return r.address_needs_review ? idx : -1;
      }
    })
    .filter(idx => idx !== -1);
  const currentReviewPosition = reviewIndex !== null ? reviewRecordNumbers.indexOf(reviewIndex) + 1 : 0;

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      // Fetch ALL records using batched queries to bypass Supabase's 1000-row default limit
      const allData: any[] = [];
      const batchSize = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('county_research')
          .select('*')
          .is('deleted_at', null)
          .order('filing_date', { ascending: true })
          .order('created_at', { ascending: true })
          .range(offset, offset + batchSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allData.push(...data);
          offset += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }

      const records = allData as CountyResearchRecord[];
      
      // Auto-fix: mark records with seller/beneficiary addresses as completed
      const needsStatusFix = records.filter(r => 
        getSellerAddress(r).trim() !== '' && r.status !== 'completed'
      );
      if (needsStatusFix.length > 0) {
        await Promise.all(needsStatusFix.map(r =>
          supabase.from('county_research').update({ status: 'completed' }).eq('id', r.id)
        ));
        records.forEach(r => {
          if (getSellerAddress(r).trim() !== '' && r.status !== 'completed') {
            r.status = 'completed';
          }
        });
      }

      // Auto-fix: revert records WITHOUT seller/beneficiary addresses back from completed to new
      const wronglyCompleted = records.filter(r => 
        getSellerAddress(r).trim() === '' && r.status === 'completed'
      );
      if (wronglyCompleted.length > 0) {
        await Promise.all(wronglyCompleted.map(r =>
          supabase.from('county_research').update({ status: 'new' }).eq('id', r.id)
        ));
        wronglyCompleted.forEach(r => {
          r.status = 'new';
        });
      }
      
      setRecords(records);
    } catch (error) {
      console.error("Error loading records:", error);
      toast.error("Failed to load records");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleDelete = async (recordId: string) => {
    if (!confirm("Are you sure you want to delete this record? It will stay deleted permanently.")) {
      return;
    }

    try {
      // Soft delete - set deleted_at timestamp instead of removing record
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', recordId);

      if (error) throw error;

      toast.success("Record deleted successfully");
      setSelectedIds(new Set());
      loadRecords();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handleManualScreenshotUpload = async (record: CountyResearchRecord, file: File) => {
    setUploadingRecordId(record.id);
    const targetLabel = isDotCounty(record.county) ? 'beneficiary/lender' : 'seller';
    toast.info(`📤 Uploading screenshot and extracting ${targetLabel} mailing address...`, { duration: 3000 });

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]); // Remove data:image/...;base64, prefix
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);

      const base64Image = await base64Promise;

      // Preview-only extraction in review mode; DB is only updated by Save
      const { data, error } = await supabase.functions.invoke('extract-contract-screenshot', {
        body: {
          recordId: record.id,
          county: record.county,
          imageData: base64Image,
          previewOnly: true,
        },
      });

      if (error) throw error;

      // DOT counties MUST use beneficiary/lender address only (grantee_address)
      const extractedAddress = (isDotCounty(record.county)
        ? data?.extractedData?.grantee_address
        : (data?.extractedData?.grantor_address || data?.extractedData?.grantee_address)) as string | undefined;

      if (data?.success && extractedAddress) {
        toast.success(
          `✅ ${isDotCounty(record.county) ? 'Beneficiary/Lender' : 'Seller'} address extracted - Click Save to confirm`,
          { duration: 4000 }
        );
        // Populate the manual address input (but do not auto-save)
        setManualAddress(extractedAddress);
      } else {
        toast.error(`Couldn't find a ${targetLabel} mailing address in that screenshot`);
      }
    } catch (error) {
      console.error("Error uploading screenshot:", error);
      toast.error("Failed to process screenshot");
    } finally {
      setUploadingRecordId(null);
    }
  };

  const handlePasteScreenshot = async (e: ClipboardEvent, record: CountyResearchRecord) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        if (blob) {
          await handleManualScreenshotUpload(record, blob);
        }
        break;
      }
    }
  };

  // Clear address field whenever we move to a new record
  useEffect(() => {
    if (isReviewMode && reviewIndex !== null) {
      setManualAddress('');
      
      const handlePaste = (e: ClipboardEvent) => {
        handlePasteScreenshot(e, displayRecords[reviewIndex]);
      };
      
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [isReviewMode, reviewIndex]);

  const handleCaptureAndExtract = async (record: CountyResearchRecord) => {
    if (!record.pdf_url) {
      toast.error("No document URL available for this record");
      return;
    }

    setCapturingRecordId(record.id);
    toast.info("📸 Capturing screenshot and extracting address...", { duration: 3000 });

    try {
      const { data, error } = await supabase.functions.invoke('capture-and-extract-address', {
        body: {
          recordId: record.id,
          documentUrl: record.pdf_url
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`✅ Address extracted: ${data.address}`, { duration: 5000 });
        loadRecords();
      } else {
        toast.error(data.error || "Failed to extract address");
      }
    } catch (error) {
      console.error("Error capturing and extracting:", error);
      toast.error("Failed to capture screenshot and extract address");
    } finally {
      setCapturingRecordId(null);
    }
  };

  const handleDeleteRecentImport = async () => {
    const recentRecords = displayRecords.filter(r => 
      r.document_number?.startsWith('UNKNOWN-')
    );

    if (recentRecords.length === 0) {
      toast.error("No recent import records found to delete");
      return;
    }

    if (!confirm(`Delete ${recentRecords.length} incorrectly imported records? This cannot be undone.`)) {
      return;
    }

    console.log('Deleting records:', recentRecords.map(r => ({ id: r.id, doc: r.document_number })));

    try {
      const idsToDelete = recentRecords.map(r => r.id);
      console.log(`Attempting to delete ${idsToDelete.length} records`);
      
      // Delete in very small batches to avoid URL length issues
      const batchSize = 5;
      let deletedCount = 0;
      let failedCount = 0;
      
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        console.log(`Deleting batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(idsToDelete.length / batchSize)}`);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
          failedCount += batch.length;
        } else {
          deletedCount += count || 0;
          console.log(`Batch deleted successfully: ${count} records`);
        }
      }
      
      if (failedCount > 0) {
        toast.error(`Deleted ${deletedCount} records, ${failedCount} failed`);
      } else {
        toast.success(`Successfully deleted ${deletedCount} records`);
      }

      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error(`Failed to delete records: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNonDickinson = async () => {
    const nonDickinsonRecords = displayRecords.filter(r => 
      r.county?.toLowerCase() !== 'dickinson'
    );

    if (nonDickinsonRecords.length === 0) {
      toast.error("No non-Dickinson County records found to delete");
      return;
    }

    if (!confirm(`Delete ${nonDickinsonRecords.length} non-Dickinson County records? This cannot be undone.`)) {
      return;
    }

    try {
      const idsToDelete = nonDickinsonRecords.map(r => r.id);
      
      const batchSize = 10;
      let deletedCount = 0;
      
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
        } else {
          deletedCount += count || 0;
        }
      }
      
      toast.success(`Successfully deleted ${deletedCount} non-Dickinson County records`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select records to delete");
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedIds.size} record(s)? They will stay deleted permanently.`)) {
      return;
    }

    try {
      // Soft delete - set deleted_at timestamp
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', Array.from(selectedIds));

      if (error) throw error;

      toast.success(`Successfully deleted ${selectedIds.size} record(s)`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
  };

  const handleRestoreDeleted = async () => {
    if (countyFilter === 'all') {
      toast.error("Please select a specific county first to restore its deleted records");
      return;
    }

    try {
      // Count deleted records for this county
      const { data: deletedRecords, error: countError } = await supabase
        .from('county_research')
        .select('id')
        .eq('county', countyFilter.toLowerCase())
        .not('deleted_at', 'is', null);

      if (countError) throw countError;

      const count = deletedRecords?.length || 0;
      if (count === 0) {
        toast.info(`No deleted records found for ${countyFilter}`);
        return;
      }

      if (!confirm(`Restore ${count} deleted records for ${countyFilter}?`)) return;

      const ids = deletedRecords!.map(r => r.id);
      const batchSize = 50;
      let restored = 0;

      for (let i = 0; i < ids.length; i += batchSize) {
        const batch = ids.slice(i, i + batchSize);
        const { error } = await supabase
          .from('county_research')
          .update({ deleted_at: null })
          .in('id', batch);
        if (error) throw error;
        restored += batch.length;
      }

      toast.success(`Restored ${restored} records for ${countyFilter}`);
      await loadRecords();
    } catch (error) {
      console.error("Error restoring records:", error);
      toast.error("Failed to restore records");
    }
  };

  const handleDeleteAllRecords = async () => {
    const totalCount = allRecords.length;
    
    if (totalCount === 0) {
      toast.error("No records to delete");
      return;
    }

    if (!confirm(`⚠️ WARNING: This will permanently delete ALL ${totalCount} records from the county research database. This action CANNOT be undone. Type "DELETE ALL" to confirm.`)) {
      return;
    }

    const secondConfirm = prompt(`Type "DELETE ALL" to confirm deletion of ${totalCount} records:`);
    if (secondConfirm !== "DELETE ALL") {
      toast.error("Deletion cancelled - confirmation text did not match");
      return;
    }

    try {
      setIsLoading(true);
      
      // Delete all records in batches
      const batchSize = 100;
      const allIds = allRecords.map(r => r.id);
      let deletedCount = 0;
      
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
          throw error;
        } else {
          deletedCount += count || 0;
        }
      }
      
      toast.success(`Successfully deleted all ${deletedCount} records from county research`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting all records:", error);
      toast.error("Failed to delete all records");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayRecords.map(r => r.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkCountyUpdate = async (fromCounty: string, toCounty: string, addressPattern: string) => {
    const recordsToUpdate = allRecords.filter(r => 
      r.county === fromCounty && 
      (r.property_address?.includes(addressPattern) || r.grantee_address?.includes(addressPattern))
    );

    if (recordsToUpdate.length === 0) {
      toast.error(`No ${fromCounty} records found with "${addressPattern}" in address`);
      return;
    }

    if (!confirm(`Update ${recordsToUpdate.length} records from "${fromCounty}" to "${toCounty}"?`)) {
      return;
    }

    try {
      const updates = recordsToUpdate.map(record =>
        supabase
          .from('county_research')
          .update({ county: toCounty, updated_at: new Date().toISOString() })
          .eq('id', record.id)
      );

      await Promise.all(updates);
      toast.success(`Updated ${recordsToUpdate.length} records to ${toCounty}`);
      loadRecords();
    } catch (error: any) {
      console.error('Bulk county update error:', error);
      toast.error("Failed to update counties");
    }
  };

  const handleExportToExcel = () => {
    try {
      // Only export records that have a seller/beneficiary address
      const recordsWithAddress = displayRecords.filter(record => getSellerAddress(record).trim() !== '');

      if (recordsWithAddress.length === 0) {
        toast.error('No records with seller/beneficiary addresses to export');
        return;
      }

      const exportData = recordsWithAddress.map(record => ({
        'County': record.county || '',
        'Document Number': record.document_number,
        'Filing Date': format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy'),
        'Lender/Seller Name': getSellerName(record),
        'Lender/Seller Address': getSellerAddress(record),
        'Borrower/Buyer Name': getBorrowerName(record),
        'Borrower/Buyer Address': getBorrowerAddress(record),
        'Property Address': record.property_address || '',
        'Loan Amount': record.loan_amount || '',
        'Confidence Score': record.confidence_score || 0,
        'Status': record.is_active ? 'Active' : 'Inactive',
        'Address Needs Review': record.address_needs_review ? 'Yes' : 'No',
        'Notes': record.notes || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'County Research');
      
      const fileName = `county-research-${format(new Date(), 'yyyy-MM-dd-HHmm')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`Exported ${exportData.length} records with addresses (skipped ${displayRecords.length - recordsWithAddress.length} without)`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  const handleExportResearchSheet = (
    mode: 'date' | 'lastname' = 'lastname',
    docFilter: 'all' | 'lc' | 'mlc' = 'all',
  ) => {
    try {
      if (displayRecords.length === 0) {
        toast.error('No records to export');
        return;
      }

      const lastNameOf = (full: string) => {
        const first = (full || '').split(';')[0].trim();
        if (!first) return '';
        if (first.includes(',')) return first.split(',')[0].trim().toUpperCase();
        const parts = first.split(/\s+/);
        return (parts[parts.length - 1] || '').toUpperCase();
      };

      const filtered = displayRecords.filter((r) => {
        if (docFilter === 'all') return true;
        const t = (r.document_type || '').toUpperCase();
        const isMemo = t.includes('MEMO');
        return docFilter === 'mlc' ? isMemo : !isMemo;
      });

      if (filtered.length === 0) {
        toast.error('No matching records for that document type');
        return;
      }

      const sorted = [...filtered].sort((a, b) => {
        if (mode === 'date') {
          return (a.filing_date || '').localeCompare(b.filing_date || '');
        }
        return lastNameOf(getSellerName(a)).localeCompare(lastNameOf(getSellerName(b)));
      });

      const countyName = (sorted[0]?.county || 'county').toUpperCase();

      const exportData = sorted.map((record) => ({
        'Filing Date': record.filing_date
          ? format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy')
          : '',
        'Doc #': record.document_number || '',
        'Seller Name': getSellerName(record),
        'Address (write in)': '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 32 }, { wch: 70 }];
      worksheet['!rows'] = [{ hpt: 22 }];
      for (let i = 0; i < sorted.length; i++) worksheet['!rows']!.push({ hpt: 42 });


      const workbook = XLSX.utils.book_new();
      const sheetName = mode === 'date' ? 'By Filing Date' : 'By Last Name';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const docTag = docFilter === 'lc' ? 'LC_' : docFilter === 'mlc' ? 'MLC_' : '';
      const suffix = mode === 'date' ? 'by_FilingDate' : 'by_LastName';
      const fileName = `${countyName}_Research_Sheet_${docTag}${suffix}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(`Exported ${exportData.length} records (${sheetName}) → ${fileName}`);
    } catch (error) {
      console.error('Error exporting research sheet:', error);
      toast.error('Failed to export research sheet');
    }
  };



  const handleSendToMailingList = async () => {
    // Determine which records to process
    let candidateRecords: CountyResearchRecord[];
    
    if (selectedIds.size > 0) {
      // Use checkbox-selected records
      candidateRecords = displayRecords.filter(r => selectedIds.has(r.id));
    } else {
      candidateRecords = displayRecords;
    }

    // DOT counties: Beneficiary/Lender = Grantee (our target), Grantor = Borrower
    // Non-DOT counties: Grantor = Seller (our target)

    const normalizeAddress = (value?: string | null) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const recordsWithAddress = candidateRecords.filter(record => getSellerAddress(record).trim() !== '');

    // Safety check for DOT counties: if borrower/grantee addresses are identical, skip auto-mailing.
    // These records usually need manual correction before Melissa.
    const ambiguousDotIds = new Set(
      recordsWithAddress
        .filter(record => {
          if (!isDotCounty(record.county)) return false;
          const grantee = normalizeAddress(record.grantee_address);
          const grantor = normalizeAddress(record.grantor_address);
          return grantee !== '' && grantor !== '' && grantee === grantor;
        })
        .map(record => record.id)
    );

    const cleanRecordsWithAddress = recordsWithAddress.filter(record => !ambiguousDotIds.has(record.id));

    if (ambiguousDotIds.size > 0) {
      toast.warning(`Skipped ${ambiguousDotIds.size} DOT records where borrower and beneficiary addresses are identical. Review/edit those records first.`);
    }

    if (cleanRecordsWithAddress.length === 0) {
      toast.error('No records with valid seller/beneficiary addresses to send');
      return;
    }

    // If no checkboxes selected, ask how many to send
    let recordsToSend = cleanRecordsWithAddress;
    if (selectedIds.size === 0) {
      const countStr = prompt(
        `${cleanRecordsWithAddress.length} records have addresses.\n\nHow many do you want to send through Melissa?\n(Enter a number, or leave blank for all)`
      );
      if (countStr === null) return; // cancelled
      
      if (countStr.trim() !== '') {
        const count = parseInt(countStr.trim(), 10);
        if (isNaN(count) || count <= 0) {
          toast.error('Please enter a valid number');
          return;
        }
        recordsToSend = cleanRecordsWithAddress.slice(0, count);
      }
    }

    if (!confirm(`Send ${recordsToSend.length} records to Mailing List via Melissa verification?`)) {
      return;
    }

    setIsSendingToMailing(true);
    toast.info(`Processing ${recordsToSend.length} records through Melissa...`, { duration: 5000 });

    try {
      // For DOT counties, use grantee (Beneficiary/Lender); for others use grantor (Seller)
      const melissaRecords = recordsToSend.map(record => {
        const sellerAddress = getSellerAddress(record);
        const sellerName = getSellerName(record);
        
        // Strip county references from address (e.g., "Collin County") — Melissa doesn't need them
        const rawAddress = sellerAddress
          .replace(/,?\s*\w+\s+County\b,?\s*(Texas|TX)?\s*/gi, ', ')
          .replace(/,\s*,/g, ',')
          .replace(/,\s*$/, '')
          .trim();

        return {
          seller_id: `${record.county}-${record.document_number}`,
          full_name: sellerName,
          free_form_address: rawAddress,
          county: record.county || '',
          recorded_date: record.filing_date,
          document_type: record.document_type || 'DEED OF TRUST',
          property_address: record.property_address || '',
          consideration: record.loan_amount || undefined,
        };
      });

      // Send through Melissa
      const { data: melissaResult, error: melissaError } = await supabase.functions.invoke('melissa-address-verify', {
        body: { records: melissaRecords }
      });

      if (melissaError) throw melissaError;

      if (!melissaResult?.success) {
        throw new Error(melissaResult?.error || 'Melissa verification failed');
      }

      const verifiedRecords = melissaResult.records || [];
      const goodAndReview = verifiedRecords.filter((r: any) => r.status !== 'bad');
      const badCount = verifiedRecords.filter((r: any) => r.status === 'bad').length;

      if (goodAndReview.length === 0) {
        toast.error(`All ${verifiedRecords.length} records failed Melissa verification`);
        setIsSendingToMailing(false);
        return;
      }

      // Upsert into mailing_records — use Melissa-parsed names and move address if available
      const mailingRecords = goodAndReview.map((r: any) => {
        const hasMoveAddress = r.md_move_address && r.md_move_address.trim() !== '';
        return {
          seller_id: r.seller_id,
          first_name: r.md_first_name || r.first_name || '',
          last_name: r.md_last_name || r.last_name || '',
          street_address: hasMoveAddress ? r.md_move_address : (r.md_address || ''),
          city: hasMoveAddress ? r.md_move_city : (r.md_city || ''),
          state: hasMoveAddress ? r.md_move_state : (r.md_state || ''),
          zip_code: (hasMoveAddress ? r.md_move_zip : (r.md_zip || '')).substring(0, 5),
          county: (r.county || '').replace(/^\w/, (c: string) => c.toUpperCase()),
          recorded_date: r.recorded_date || null,
          document_type: r.document_type || '',
          property_address: r.property_address || '',
          consideration: r.consideration || null,
          phone_number: r.md_phone || null,
          mailing_status: 'active',
        };
      });
      
      const movedCount = goodAndReview.filter((r: any) => r.md_move_address && r.md_move_address.trim() !== '').length;
      const phoneCount = goodAndReview.filter((r: any) => r.md_phone).length;

      // Batch upsert into mailing_records
      const batchSize = 100;
      let insertedCount = 0;
      for (let i = 0; i < mailingRecords.length; i += batchSize) {
        const batch = mailingRecords.slice(i, i + batchSize);
        const { error: upsertError } = await supabase
          .from('mailing_records')
          .upsert(batch, { onConflict: 'seller_id', ignoreDuplicates: false });

        if (upsertError) {
          console.error('Upsert batch error:', upsertError);
          // Try individual inserts for failed batch
          for (const record of batch) {
            const { error: singleError } = await supabase
              .from('mailing_records')
              .upsert(record, { onConflict: 'seller_id', ignoreDuplicates: false });
            if (!singleError) insertedCount++;
          }
        } else {
          insertedCount += batch.length;
        }
      }

      const parts = [`✅ ${insertedCount} records added to Mailing List`];
      if (movedCount > 0) parts.push(`${movedCount} address changes applied`);
      if (phoneCount > 0) parts.push(`${phoneCount} phone numbers appended`);
      if (badCount > 0) parts.push(`${badCount} bad addresses skipped`);
      toast.success(parts.join(' · '), { duration: 6000 });
    } catch (error) {
      console.error('Error sending to mailing list:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send to mailing list');
    } finally {
      setIsSendingToMailing(false);
    }
  };

  const handleOpenNameCleanup = () => {
    let candidates: CountyResearchRecord[];
    if (selectedIds.size > 0) {
      candidates = displayRecords.filter(r => selectedIds.has(r.id));
    } else {
      candidates = displayRecords.filter(r => getSellerAddress(r).trim() !== '');
    }
    if (candidates.length === 0) {
      toast.error('No records with addresses to clean');
      return;
    }
    setNameCleanupRecords(candidates);
    setShowNameCleanup(true);
  };

  const handleNameCleanupComplete = (cleaned: CleanedRecord[]) => {
    setShowNameCleanup(false);
    // Export cleaned names — Sheet 1 = Melissa-ready upload, Sheet 2 = full reference data
    try {
      // ----- Sheet 1: Melissa Personator / Listware bulk-upload format -----
      // Standard column headers Melissa Data accepts on their upload page:
      // RecordID, FirstName, LastName, CompanyName, AddressLine1, AddressLine2, City, State, PostalCode, Country
      const melissaRows = cleaned.map((c, idx) => ({
        RecordID: c.record.document_number || String(idx + 1),
        FirstName: c.isBusiness ? '' : (c.firstName || ''),
        LastName: c.isBusiness ? '' : (c.lastName || ''),
        CompanyName: c.isBusiness ? (c.businessName || '') : '',
        AddressLine1: c.street || '',
        AddressLine2: '',
        City: c.city || '',
        State: c.state || '',
        PostalCode: c.zip || '',
        Country: 'US',
      }));

      const melissaSheet = XLSX.utils.json_to_sheet(melissaRows);
      melissaSheet['!cols'] = [
        { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 28 }, { wch: 32 },
        { wch: 14 }, { wch: 20 }, { wch: 6 }, { wch: 10 }, { wch: 8 },
      ];

      // ----- Sheet 2: Full cleaned reference (kept for your records) -----
      const referenceRows = cleaned.map((c, idx) => {
        const nameParts = [c.prefix === 'none' ? '' : c.prefix, c.firstName, c.lastName].filter(Boolean).join(' ');
        const mailLabel = c.isBusiness
          ? c.businessName
          : c.suffix ? `${nameParts}, ${c.suffix}` : nameParts;
        return {
          '#': idx + 1,
          'Prefix': c.isBusiness ? '' : (c.prefix === 'none' ? '' : c.prefix),
          'First Name': c.isBusiness ? '' : c.firstName,
          'Last Name': c.isBusiness ? '' : c.lastName,
          'Suffix': c.suffix || '',
          'Business Name': c.isBusiness ? c.businessName : '',
          'Mail Label': mailLabel,
          'Greeting': c.isBusiness ? '' : `Hi ${c.firstName} –`,
          'Original Name': getSellerName(c.record),
          'Street Address': c.street || '',
          'City': c.city || '',
          'State': c.state || '',
          'Zip': c.zip || '',
          'Doc #': c.record.document_number,
          'Filing Date': c.record.filing_date,
          'County': (c.record.county || '').replace(/^\w/, (ch: string) => ch.toUpperCase()),
          'Loan Amount': c.record.loan_amount || '',
          'Type': c.isBusiness ? 'Business' : (c.isCouple ? 'Couple' : 'Person'),
        };
      });
      const referenceSheet = XLSX.utils.json_to_sheet(referenceRows);
      referenceSheet['!cols'] = [
        { wch: 4 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 12 }, { wch: 28 },
        { wch: 30 }, { wch: 18 }, { wch: 30 }, { wch: 30 }, { wch: 18 }, { wch: 6 },
        { wch: 8 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 10 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, melissaSheet, 'Melissa Upload');
      XLSX.utils.book_append_sheet(workbook, referenceSheet, 'Reference');
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const fileName = `Melissa_Upload_${dateStr}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      // Also drop a CSV version of just the Melissa sheet — Melissa's bulk uploader prefers CSV
      const csv = XLSX.utils.sheet_to_csv(melissaSheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Melissa_Upload_${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${melissaRows.length} records — Melissa-ready .xlsx + .csv downloaded`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export cleaned names');
    }
  };

  const handleNameCleanupSaveProgress = async (cleaned: CleanedRecord[]) => {
    if (cleaned.length === 0) {
      toast.info('No records to save');
      setShowNameCleanup(false);
      return;
    }

    try {
      type CountyUpdate = { id: string; grantor_name?: string; grantor_address?: string; grantee_name?: string; grantee_address?: string };
      const updates: CountyUpdate[] = cleaned.map(c => {
        const nameParts = [c.prefix === 'none' ? '' : c.prefix, c.firstName, c.lastName].filter(Boolean);
        const displayName = c.isBusiness
          ? c.businessName
          : c.suffix
            ? `${nameParts.join(' ')}, ${c.suffix}`
            : nameParts.join(' ');
        const address = [c.street, c.city && c.state ? `${c.city}, ${c.state} ${c.zip}`.trim() : ''].filter(Boolean).join(', ');
        const isDot = isDotCounty(c.record.county);
        return isDot
          ? { id: c.record.id, grantee_name: displayName, grantee_address: address }
          : { id: c.record.id, grantor_name: displayName, grantor_address: address };
      });

      let savedCount = 0;
      for (const update of updates) {
        const { id, ...fields } = update;
        const { error } = await supabase
          .from('county_research')
          .update(fields)
          .eq('id', id);
        if (error) throw error;
        savedCount += 1;
      }

      toast.success(`Saved ${savedCount} cleaned records to the database`);
      setShowNameCleanup(false);
      await loadRecords();
      onRefresh();
    } catch (error) {
      console.error('Error saving cleaned records:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save cleaned records');
    }
  };

  const handleSaveToGoogleSheets = async () => {
    if (!spreadsheetId.trim()) {
      toast.error("Spreadsheet ID required. Please enter your Google Spreadsheet ID");
      return;
    }

    if (displayRecords.length === 0) {
      toast.error("No data to save");
      return;
    }

    setIsSavingToSheets(true);

    try {
      const { data, error } = await supabase.functions.invoke('save-to-google-sheets', {
        body: {
          records: displayRecords,
          spreadsheetId: spreadsheetId.trim(),
          sheetName: 'County Research',
        },
      });

      if (error) throw error;

      toast.success(`Saved ${displayRecords.length} records to Google Sheets`);
    } catch (error: any) {
      console.error('Error saving to Google Sheets:', error);
      toast.error(error.message || "Failed to save to Google Sheets");
    } finally {
      setIsSavingToSheets(false);
    }
  };

  const startReviewMode = () => {
    // Clear any stale address state first
    setManualAddress('');
    const recordsNeedingReview = displayRecords.filter(r => r.address_needs_review);
    if (recordsNeedingReview.length > 0) {
      const firstIndex = displayRecords.findIndex(r => r.address_needs_review);
      setReviewIndex(firstIndex);
      setIsReviewMode(true);
    } else {
      toast.error("No records need address review");
    }
  };

  const closeReviewMode = () => {
    setIsReviewMode(false);
    setReviewIndex(null);
    setManualAddress('');
  };

  const handleSaveAndNext = async () => {
    if (reviewIndex === null) return;
    
    const currentRecord = displayRecords[reviewIndex];
    const addressToSave = manualAddress.trim();
    
    if (!addressToSave) {
      toast.error("Please enter an address");
      return;
    }

    const isDotRecord = isDotCounty(currentRecord.county);
    const manualAddressUpdate = isDotRecord
      ? { grantee_address: addressToSave }
      : { grantor_address: addressToSave };

    // DUPLICATE GUARD: Check if this exact address already belongs to a DIFFERENT record in this county
    const duplicateRecord = records.find(r => {
      if (r.id === currentRecord.id || r.county !== currentRecord.county) return false;
      const existingAddress = isDotRecord ? (r.grantee_address || '') : (r.grantor_address || '');
      return existingAddress === addressToSave;
    });
    if (duplicateRecord) {
      toast.error(`⚠️ This address is already assigned to ${getSellerName(duplicateRecord) || duplicateRecord.document_number}. Please enter the correct address for this record.`);
      return;
    }

    const previousManualAddress = manualAddress;
    const previousRecords = [...records];

    // Find the next record BEFORE updating state
    let nextIndex = -1;
    if ((currentRecord.county || '').toLowerCase() === 'comal') {
      nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && (r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '');
    } else if (!currentRecord.address_needs_review) {
      nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && getSellerAddress(r).trim() === '');
    } else {
      nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && r.address_needs_review);
    }

    const optimisticRecords = records.map(r =>
      r.id === currentRecord.id
        ? {
            ...r,
            ...(isDotRecord ? { grantee_address: addressToSave } : { grantor_address: addressToSave }),
            address_needs_review: false,
            address_extraction_method: 'manual',
            address_confidence_score: 100,
            status: 'completed'
          }
        : r
    );

    setManualAddress('');
    setRecords(optimisticRecords);

    try {
      console.log('Saving address for record:', currentRecord.id, 'Address:', addressToSave, 'Field:', isDotRecord ? 'grantee_address' : 'grantor_address');

      const { data, error } = await supabase
        .from('county_research')
        .update({
          ...manualAddressUpdate,
          address_needs_review: false,
          address_extraction_method: 'manual',
          address_confidence_score: 100,
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', currentRecord.id)
        .select('id')
        .single();

      if (error) {
        console.error('Database update error:', error);
        throw error;
      }

      if (!data?.id) {
        throw new Error('Address save was not persisted');
      }

      console.log('Database update confirmed for record:', data.id);
      
      toast.success("Address saved!");
      
      // Move to next record - input is already cleared
      if (nextIndex !== -1) {
        setReviewIndex(nextIndex);
      } else {
        closeReviewMode();
        toast.success("Finished reviewing all records!");
      }
    } catch (error) {
      console.error("Error saving address:", error);
      setRecords(previousRecords);
      setManualAddress(previousManualAddress);
      toast.error("Failed to save address — reverted local changes");
    }
  };

  const handleDeleteAndNext = async () => {
    if (reviewIndex === null) return;
    
    const currentRecord = displayRecords[reviewIndex];
    
    try {
      // Soft delete - set deleted_at timestamp
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', currentRecord.id);

      if (error) throw error;

      toast.success("Record deleted");
      
      // Remove record from local state immediately (don't reload - that causes index drift)
      const updatedRecords = records.filter(r => r.id !== currentRecord.id);
      setRecords(updatedRecords);
      
      // Recompute displayRecords from updated list
      let updatedDisplay = updatedRecords;
      if (countyFilter !== "all") {
        updatedDisplay = updatedDisplay.filter(r => r.county?.toLowerCase() === countyFilter.toLowerCase());
      }
      if (statusFilter === "melissa") {
        updatedDisplay = updatedDisplay.filter(r => r.status === 'ready_for_melissa');
      } else if (statusFilter === "needs_review") {
        updatedDisplay = updatedDisplay.filter(r => r.address_needs_review);
      }
      
      // Check if current record was Comal to determine review mode type
      const isComalMode = (currentRecord.county || '').toLowerCase() === 'comal';
      const isAddressMode = !currentRecord.address_needs_review;
      
      // Find the next record in the UPDATED list, starting from same position (since we removed one)
      const searchStart = Math.min(reviewIndex, updatedDisplay.length - 1);
      const nextIndex = updatedDisplay.findIndex((r, idx) => 
        idx >= searchStart && 
        (isComalMode ? ((r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '') : isAddressMode ? getSellerAddress(r).trim() === '' : r.address_needs_review)
      );
      
      setManualAddress('');
      
      if (nextIndex !== -1) {
        setReviewIndex(nextIndex);
        setManualAddress('');
      } else {
        closeReviewMode();
        toast.success("Finished reviewing all records!");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handlePrevious = () => {
    if (reviewIndex === null || reviewIndex === 0) return;
    
    // Check if current record is Comal to determine review mode type
    const isComalMode = (displayRecords[reviewIndex]?.county || '').toLowerCase() === 'comal';
    const isAddressMode = !displayRecords[reviewIndex]?.address_needs_review;
    
    const prevIndex = displayRecords.slice(0, reviewIndex).reverse().findIndex(r => 
      isComalMode ? ((r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '') : isAddressMode ? getSellerAddress(r).trim() === '' : r.address_needs_review
    );
    
    if (prevIndex !== -1) {
      const actualIndex = reviewIndex - prevIndex - 1;
      setReviewIndex(actualIndex);
      setManualAddress('');
    }
  };

  const handleNext = () => {
    if (reviewIndex === null) return;
    
    const isComalMode = (displayRecords[reviewIndex]?.county || '').toLowerCase() === 'comal';
    const isAddressMode = !displayRecords[reviewIndex]?.address_needs_review;
    
    const nextIndex = displayRecords.findIndex((r, idx) => 
      idx > reviewIndex && (isComalMode ? ((r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '') : isAddressMode ? getSellerAddress(r).trim() === '' : r.address_needs_review)
    );
    
    if (nextIndex !== -1) {
      setReviewIndex(nextIndex);
      setManualAddress('');
    }
  };

  const handleInlineEdit = (record: CountyResearchRecord) => {
    setEditingRecord({
      id: record.id,
      grantor_name: record.grantor_name || '',
      grantee_name: record.grantee_name || '',
      grantor_address: record.grantor_address || '',
      grantee_address: record.grantee_address || '',
      property_address: record.property_address || '',
      loan_amount: record.loan_amount || undefined,
      interest_rate: record.interest_rate || undefined,
      monthly_payment: record.monthly_payment || undefined,
      term_months: record.term_months || undefined,
      balloon_amount: record.balloon_amount || undefined,
      notes: record.notes || '',
    });
  };

  const handleSaveInlineEdit = async () => {
    if (!editingRecord) return;
    try {
      const { error } = await supabase
        .from('county_research')
        .update({
          grantor_name: editingRecord.grantor_name || null,
          grantee_name: editingRecord.grantee_name || null,
          grantor_address: editingRecord.grantor_address || null,
          grantee_address: editingRecord.grantee_address || null,
          property_address: editingRecord.property_address || null,
          loan_amount: editingRecord.loan_amount || null,
          interest_rate: editingRecord.interest_rate || null,
          monthly_payment: editingRecord.monthly_payment || null,
          term_months: editingRecord.term_months || null,
          balloon_amount: editingRecord.balloon_amount || null,
          notes: editingRecord.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingRecord.id);

      if (error) throw error;
      toast.success("Record updated!");
      setEditingRecord(null);
      loadRecords();
    } catch (error) {
      console.error("Error updating record:", error);
      toast.error("Failed to update record");
    }
  };

  const handleQuickAddressSave = async (record: CountyResearchRecord) => {
    if (!quickEditId) return;
    try {
      const isDot = isDotCounty(record.county);
      const addressField = isDot ? 'grantee_address' : 'grantor_address';
      const updatePayload: Record<string, any> = {
        [addressField]: quickEditAddress.trim() || null,
        address_extraction_method: 'manual',
        address_needs_review: false,
        status: quickEditAddress.trim() ? 'completed' : 'new',
        updated_at: new Date().toISOString(),
      };
      // If clearing, also clear the other potential field
      if (!quickEditAddress.trim()) {
        updatePayload[isDot ? 'grantor_address' : 'grantee_address'] = null;
      }
      const { error } = await supabase
        .from('county_research')
        .update(updatePayload)
        .eq('id', record.id);
      if (error) throw error;
      toast.success("Address updated!");
      setQuickEditId(null);
      setQuickEditAddress('');
      loadRecords();
    } catch (err) {
      console.error("Quick edit error:", err);
      toast.error("Failed to update address");
    }
  };

  const handleQuickLoanSave = async (record: CountyResearchRecord) => {
    if (!quickEditLoanId) return;
    try {
      const parsedAmount = quickEditLoanAmount.trim() ? parseFloat(quickEditLoanAmount.replace(/[^0-9.-]/g, '')) : null;
      const { error } = await supabase
        .from('county_research')
        .update({
          loan_amount: parsedAmount,
          updated_at: new Date().toISOString(),
        })
        .eq('id', record.id);
      if (error) throw error;
      toast.success("Loan amount updated!");
      setQuickEditLoanId(null);
      setQuickEditLoanAmount('');
      loadRecords();
    } catch (err) {
      console.error("Quick loan edit error:", err);
      toast.error("Failed to update loan amount");
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isReviewMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger shortcuts if Ctrl is held AND the key matches
      // This prevents accidental triggers from paste operations
      if (!e.ctrlKey) return;
      
      if (e.key === 'Delete') {
        e.preventDefault();
        handleDeleteAndNext();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveAndNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReviewMode, reviewIndex, displayRecords, manualAddress]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Loading records...</p>
        </CardContent>
      </Card>
    );
  }

  if (displayRecords.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground">No research records yet. Start capturing contracts{defaultCountyFilter ? ` from ${defaultCountyFilter.charAt(0).toUpperCase() + defaultCountyFilter.slice(1)} County` : ''}.</p>
        </CardContent>
      </Card>
    );
  }

  const ownerFinanceCount = displayRecords.filter(r => r.is_potential_owner_finance).length;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Extracted Records</CardTitle>
                <CardDescription>
                  {displayRecords.length} total records, {ownerFinanceCount} potential owner-financed
                  {selectedIds.size > 0 && ` • ${selectedIds.size} selected`}
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={countyFilter} onValueChange={setCountyFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by county" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counties</SelectItem>
                  {counties.map(county => (
                    <SelectItem key={county} value={county}>
                      {county.charAt(0).toUpperCase() + county.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="has_address">✅ Has Address ({allRecords.filter(r => (countyFilter === 'all' || r.county?.toLowerCase() === countyFilter.toLowerCase()) && getSellerAddress(r).trim() !== '').length})</SelectItem>
                  <SelectItem value="no_address">❌ Missing Address ({allRecords.filter(r => (countyFilter === 'all' || r.county?.toLowerCase() === countyFilter.toLowerCase()) && getSellerAddress(r).trim() === '').length})</SelectItem>
                  <SelectItem value="blacklisted">🚫 Blacklisted ({allRecords.filter(r => (countyFilter === 'all' || r.county?.toLowerCase() === countyFilter.toLowerCase()) && r.status === 'blacklisted').length})</SelectItem>
                  <SelectItem value="melissa">Ready for Melissa Data ({recordsReadyForMelissa.length})</SelectItem>
                  <SelectItem value="needs_review">Needs Review ({recordsNeedingReview.length})</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Google Spreadsheet ID (paste from URL)"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                className="w-80"
              />
              <Button
                variant="default"
                size="sm"
                onClick={startReviewMode}
                disabled={recordsNeedingReview.length === 0}
                className="bg-primary hover:bg-primary/90"
              >
                <Eye className="mr-1 h-4 w-4" />
                Review ({recordsNeedingReview.length})
              </Button>
              {recordsMissingAddress.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setManualAddress('');
                    const firstIndex = displayRecords.findIndex(r => getSellerAddress(r).trim() === '');
                    if (firstIndex !== -1) {
                      setReviewIndex(firstIndex);
                      setIsReviewMode(true);
                    }
                  }}
                >
                  <Camera className="mr-1 h-4 w-4" />
                  Review Addresses ({recordsMissingAddress.length})
                </Button>
              )}
              {countyFilter === 'comal' && comalRecordsNeedingReview.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setManualAddress('');
                    const firstIndex = displayRecords.findIndex(r => (r.county || '').toLowerCase() === 'comal' && getSellerAddress(r).trim() === '');
                    if (firstIndex !== -1) {
                      setReviewIndex(firstIndex);
                      setIsReviewMode(true);
                    }
                  }}
                >
                  Review Comal Addresses ({comalRecordsNeedingReview.length})
                </Button>
              )}
              {countyFilter === 'comal' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!confirm('This will clear ALL addresses for Comal County records so you can re-enter them. Continue?')) {
                      return;
                    }
                    try {
                      // Close review mode first to prevent stale data
                      closeReviewMode();
                      
                      const { data, error } = await supabase.functions.invoke('clear-comal-addresses');
                      if (error) throw error;
                      toast.success(`Cleared addresses for ${data.recordsUpdated} records`);
                      
                      // Await loadRecords and clear address state
                      await loadRecords();
                      setManualAddress('');
                    } catch (error) {
                      console.error('Error clearing addresses:', error);
                      toast.error('Failed to clear addresses');
                    }
                  }}
                  className="text-orange-600 hover:text-orange-700"
                >
                  Clear All Comal Addresses
                </Button>
              )}
              {(() => {
                const countyId = (displayRecords[0]?.county || '').toLowerCase();
                const splitByType = countyId === 'delta' || countyId === 'otsego' || countyId === 'menominee';
                if (splitByType) {
                  return (
                    <>
                      <Button variant="outline" size="sm" onClick={() => handleExportResearchSheet('date', 'lc')}>
                        <Download className="mr-2 h-4 w-4" /> LC — Date
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportResearchSheet('lastname', 'lc')}>
                        <Download className="mr-2 h-4 w-4" /> LC — Last Name
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportResearchSheet('date', 'mlc')}>
                        <Download className="mr-2 h-4 w-4" /> MLC — Date
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportResearchSheet('lastname', 'mlc')}>
                        <Download className="mr-2 h-4 w-4" /> MLC — Last Name
                      </Button>
                    </>
                  );
                }
                return (
                  <>
                    <Button variant="outline" size="sm" onClick={() => handleExportResearchSheet('date')}>
                      <Download className="mr-2 h-4 w-4" /> Sheet by Date
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportResearchSheet('lastname')}>
                      <Download className="mr-2 h-4 w-4" /> Sheet by Last Name
                    </Button>
                  </>
                );
              })()}

              <Button
                variant="outline"
                size="sm"
                onClick={handleExportToExcel}
              >
                <Download className="mr-2 h-4 w-4" />
                Export to Excel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSendToMailingList}
                disabled={isSendingToMailing}
              >
                {isSendingToMailing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                {isSendingToMailing ? 'Verifying...' : 'Melissa Send to Mail'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleOpenNameCleanup}
              >
                <Users className="mr-2 h-4 w-4" />
                Clean Names & Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveToGoogleSheets}
                disabled={isSavingToSheets || !spreadsheetId.trim()}
                title={!spreadsheetId.trim() ? "Enter Spreadsheet ID first" : "Append records to Google Sheets"}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isSavingToSheets ? "Saving..." : "Save to Google Sheets"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteRecentImport}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Failed Import
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteNonDickinson}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Non-Dickinson
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAllRecords}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete ALL Records
              </Button>
              {selectedIds.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected ({selectedIds.size})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestoreDeleted}
                className="text-green-600 hover:text-green-700 border-green-300"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Restore Deleted
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkCountyUpdate('randall', 'madison', 'London, Ohio')}
                className="text-blue-600 hover:text-blue-700"
              >
                Fix Madison Counties
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadRecords}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedIds.size === displayRecords.length && displayRecords.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>County</TableHead>
                <TableHead>Filing Date</TableHead>
                <TableHead>Doc #</TableHead>
                <TableHead>{isTexasCounty(countyFilter) ? 'Lender' : 'Seller'}</TableHead>
                <TableHead>{isTexasCounty(countyFilter) ? 'Borrower' : 'Buyer'}</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Loan Amount</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRecords.map((record) => (
                <>
                  <TableRow 
                    key={record.id} 
                    className="cursor-pointer hover:bg-muted/50" 
                    onClick={() => {
                      const newExpanded = new Set(expandedRows);
                      if (newExpanded.has(record.id)) {
                        newExpanded.delete(record.id);
                      } else {
                        newExpanded.add(record.id);
                      }
                      setExpandedRows(newExpanded);
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()} className="w-12">
                      <div className="flex items-center gap-2">
                        {expandedRows.has(record.id) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <Checkbox
                          checked={selectedIds.has(record.id)}
                          onCheckedChange={() => toggleSelect(record.id)}
                        />
                      </div>
                    </TableCell>
                  <TableCell>
                    {record.status === 'completed' ? (
                      <Badge className="bg-green-600 hover:bg-green-700">Completed</Badge>
                    ) : record.is_active ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {record.county || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy')}</TableCell>
                  <TableCell className="font-mono text-sm" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <span>{record.document_number}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText(record.document_number);
                          toast.success(`Copied: ${record.document_number}`);
                        }}
                        title="Copy document number"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      {record.county?.toLowerCase() === 'coryell' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => window.open('http://24.173.220.132/CoryellPublicRecords/Search/AdvancePagedSearch', '_blank')}
                          title="Open Coryell County records search"
                        >
                          <Search className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div>
                      <p className="font-medium">{getSellerName(record)}</p>
                      {quickEditId === record.id ? (
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={quickEditAddress}
                            onChange={(e) => setQuickEditAddress(e.target.value)}
                            className="h-7 text-xs"
                            placeholder="Enter lender address..."
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleQuickAddressSave(record);
                              if (e.key === 'Escape') { setQuickEditId(null); setQuickEditAddress(''); }
                            }}
                          />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleQuickAddressSave(record)}>
                            <Save className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setQuickEditId(null); setQuickEditAddress(''); }}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : record.address_needs_review ? (
                        <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => { setQuickEditId(record.id); setQuickEditAddress(''); }}>Address needed</Badge>
                      ) : getSellerAddress(record) ? (
                        <p 
                          className="text-xs text-muted-foreground truncate max-w-[200px] cursor-pointer hover:text-primary flex items-center gap-1"
                          onClick={() => { setQuickEditId(record.id); setQuickEditAddress(getSellerAddress(record)); }}
                          title="Click to edit address"
                        >
                          {getSellerAddress(record)}
                          <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
                        </p>
                      ) : (
                        <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => { setQuickEditId(record.id); setQuickEditAddress(''); }}>No address</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getBorrowerName(record)}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{record.property_address || 'N/A'}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {quickEditLoanId === record.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={quickEditLoanAmount}
                          onChange={(e) => setQuickEditLoanAmount(e.target.value)}
                          className="h-7 text-xs w-28"
                          placeholder="Amount..."
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleQuickLoanSave(record);
                            if (e.key === 'Escape') { setQuickEditLoanId(null); setQuickEditLoanAmount(''); }
                          }}
                        />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleQuickLoanSave(record)}>
                          <Save className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setQuickEditLoanId(null); setQuickEditLoanAmount(''); }}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <span
                        className="cursor-pointer hover:text-primary flex items-center gap-1"
                        onClick={() => { setQuickEditLoanId(record.id); setQuickEditLoanAmount(record.loan_amount?.toString() || ''); }}
                        title="Click to edit loan amount"
                      >
                        {record.loan_amount ? formatCurrency(record.loan_amount) : <Badge variant="outline" className="text-xs">N/A</Badge>}
                        <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        (record.confidence_score || 0) > 80 ? 'default' : 
                        (record.confidence_score || 0) > 60 ? 'secondary' : 
                        'outline'
                      }
                    >
                      {record.confidence_score || 0}%
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      {record.address_needs_review ? (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setSelectedRecord(record)}
                        >
                          Add Address
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedRecord(record)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              handleManualScreenshotUpload(record, file);
                            }
                          };
                          input.click();
                        }}
                        disabled={uploadingRecordId === record.id}
                        title={`Upload screenshot to extract ${isDotCounty(record.county) ? 'beneficiary/lender' : 'seller'} address`}
                      >
                        {uploadingRecordId === record.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </>
                        )}
                      </Button>
                      {record.pdf_url && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleCaptureAndExtract(record)}
                            disabled={capturingRecordId === record.id}
                            title="Capture screenshot and extract address with AI"
                          >
                            {capturingRecordId === record.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Camera className="h-4 w-4 mr-1" />
                                Auto
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(record.pdf_url, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(record.id)}
                        title="Delete record"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedRows.has(record.id) && (
                  <TableRow key={`${record.id}-expanded`}>
                    <TableCell colSpan={11} className="bg-muted/30 p-4">
                      {editingRecord?.id === record.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            {/* For DOT counties, swap roles in editing interface */}
                            {(() => {
                              const isDeedOfTrust = isDotCounty(editingRecord.county);
                              
                              if (isDeedOfTrust) {
                                return (
                                  <>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">{isTexasCounty(editingRecord.county || '') ? 'Lender (Grantee)' : 'Seller/Lender (Grantee)'}</label>
                                      <Input
                                        value={editingRecord.grantee_name || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantee_name: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">{isTexasCounty(editingRecord.county || '') ? 'Borrower (Grantor)' : 'Buyer/Borrower (Grantor)'}</label>
                                      <Input
                                        value={editingRecord.grantor_name || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantor_name: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">{isTexasCounty(editingRecord.county || '') ? 'Lender Address' : 'Seller/Lender Address'}</label>
                                      <Input
                                        value={editingRecord.grantee_address || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantee_address: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">{isTexasCounty(editingRecord.county || '') ? 'Borrower Address' : 'Buyer/Borrower Address'}</label>
                                      <Input
                                        value={editingRecord.grantor_address || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantor_address: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">Seller (Grantor)</label>
                                      <Input
                                        value={editingRecord.grantor_name || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantor_name: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">Buyer (Grantee)</label>
                                      <Input
                                        value={editingRecord.grantee_name || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantee_name: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">Seller Address</label>
                                      <Input
                                        value={editingRecord.grantor_address || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantor_address: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-semibold text-muted-foreground">Buyer Address</label>
                                      <Input
                                        value={editingRecord.grantee_address || ''}
                                        onChange={(e) => setEditingRecord({ ...editingRecord, grantee_address: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                  </>
                                );
                              }
                            })()}
                            
                            <div className="col-span-2">
                              <label className="text-xs font-semibold text-muted-foreground">Property Address</label>
                              <Input
                                value={editingRecord.property_address || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, property_address: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Loan Amount</label>
                              <Input
                                type="number"
                                value={editingRecord.loan_amount ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, loan_amount: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Interest Rate (decimal, e.g. 0.08)</label>
                              <Input
                                type="number"
                                step="0.001"
                                value={editingRecord.interest_rate ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, interest_rate: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Monthly Payment</label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editingRecord.monthly_payment ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, monthly_payment: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Term (Months)</label>
                              <Input
                                type="number"
                                value={editingRecord.term_months ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, term_months: e.target.value ? parseInt(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Balloon Amount</label>
                              <Input
                                type="number"
                                value={editingRecord.balloon_amount ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, balloon_amount: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs font-semibold text-muted-foreground">Notes</label>
                              <Textarea
                                value={editingRecord.notes || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, notes: e.target.value })}
                                className="text-sm"
                                rows={2}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveInlineEdit}>
                              <Save className="h-4 w-4 mr-1" />
                              Save Changes
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingRecord(null)}>
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex justify-end">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleInlineEdit(record); }}>
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit Record
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">{isTexasCounty(record.county || '') ? 'Lender Address' : 'Seller Address'}</p>
                              <p>{getSellerAddress(record) || 'Not available'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">{isTexasCounty(record.county || '') ? 'Borrower Address' : 'Buyer Address'}</p>
                              <p>{getBorrowerAddress(record) || 'Not available'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Interest Rate</p>
                              <p>{record.interest_rate ? `${(record.interest_rate * 100).toFixed(2)}%` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Monthly Payment</p>
                              <p>{record.monthly_payment ? formatCurrency(record.monthly_payment) : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Term</p>
                              <p>{record.term_months ? `${record.term_months} months` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Balloon Payment</p>
                              <p>{record.balloon_amount ? formatCurrency(record.balloon_amount) : 'None'}</p>
                            </div>
                            {record.balloon_due_date && (
                              <div>
                                <p className="font-semibold text-muted-foreground mb-1">Balloon Due Date</p>
                                <p>{format(new Date(record.balloon_due_date), 'MM/dd/yyyy')}</p>
                              </div>
                            )}
                            {record.legal_description && (
                              <div className="col-span-2">
                                <p className="font-semibold text-muted-foreground mb-1">Legal Description</p>
                                <p className="text-xs">{record.legal_description}</p>
                              </div>
                            )}
                            {record.notes && (
                              <div className="col-span-2">
                                <p className="font-semibold text-muted-foreground mb-1">Notes</p>
                                <p className="text-xs">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedRecord && (
        <RecordDetailModal
          record={selectedRecord}
          open={selectedRecord !== null}
          onClose={() => setSelectedRecord(null)}
          onUpdate={loadRecords}
        />
      )}

      {/* Review Mode Dialog - Narrow vertical layout for split screen */}
      {isReviewMode && reviewIndex !== null && displayRecords[reviewIndex] && (
        <Dialog open={isReviewMode} onOpenChange={(open) => !open && closeReviewMode()}>
          <DialogContent className="max-w-md w-full h-[95vh] overflow-hidden flex flex-col p-4 gap-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between gap-2 pb-2 border-b shrink-0">
              <span className="text-sm font-bold">
                {currentReviewPosition}/{activeReviewList.length}
              </span>
              <div className="flex-1 bg-secondary h-1.5 rounded-full overflow-hidden mx-2">
                <div 
                  className="bg-primary h-full transition-all duration-300"
                  style={{ width: `${(currentReviewPosition / activeReviewList.length) * 100}%` }}
                />
              </div>
              <div className="flex gap-1">
                <Button
                  onClick={handlePrevious}
                  disabled={currentReviewPosition === 1}
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={currentReviewPosition >= activeReviewList.length}
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {/* Record Info - Compact */}
              <div className="grid grid-cols-2 gap-2 p-2 bg-muted rounded text-xs">
                <div>
                  <label className="text-muted-foreground">Doc #</label>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-sm truncate">{displayRecords[reviewIndex].document_number}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[10px] shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(displayRecords[reviewIndex].document_number);
                        toast.success("Doc # copied!");
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-muted-foreground">Date</label>
                  <p>{format(new Date(displayRecords[reviewIndex].filing_date + 'T12:00:00'), 'MM/dd/yy')}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-muted-foreground">Grantor</label>
                  <p className="font-medium truncate">{displayRecords[reviewIndex].grantor_name}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-muted-foreground">Grantee</label>
                  <p className="truncate">{displayRecords[reviewIndex].grantee_name}</p>
                </div>
                {displayRecords[reviewIndex].property_address && (
                  <div className="col-span-2">
                    <label className="text-muted-foreground">Property</label>
                    <p className="truncate">{displayRecords[reviewIndex].property_address}</p>
                  </div>
                )}
              </div>

              {/* Warning Box - context-aware terminology */}
              {isDotCounty(displayRecords[reviewIndex]?.county) ? (
                <div className="text-xs bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-800 p-2 rounded">
                  <p className="font-bold text-red-700 dark:text-red-400">⚠️ Find BENEFICIARY/LENDER section</p>
                  <p className="text-red-600 dark:text-red-300 mt-1">Use the Beneficiary/Lender mailing address only (not Grantor/Borrower, not Trustee).</p>
                </div>
              ) : (
                <div className="text-xs bg-blue-50 dark:bg-blue-950/20 border border-blue-300 dark:border-blue-800 p-2 rounded">
                  <p className="font-bold text-blue-700 dark:text-blue-400">📋 Find SELLER/VENDOR mailing address</p>
                  <p className="text-blue-600 dark:text-blue-300 mt-1">Paste a screenshot of the contract to extract the seller's address</p>
                </div>
              )}

              {/* Address Input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">
                    {isDotCounty(displayRecords[reviewIndex]?.county) ? 'Beneficiary/Lender Address' : 'Seller Address'}
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file && reviewIndex !== null) {
                          handleManualScreenshotUpload(displayRecords[reviewIndex], file);
                        }
                      };
                      input.click();
                    }}
                    disabled={uploadingRecordId !== null}
                  >
                    {uploadingRecordId !== null ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3 mr-1" />
                    )}
                    Upload
                  </Button>
                </div>
                
                <Textarea
                  placeholder={isDotCounty(displayRecords[reviewIndex]?.county) ? 'Enter Beneficiary/Lender mailing address or paste screenshot (Ctrl+V)' : 'Enter Seller address or paste screenshot (Ctrl+V)'}
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  rows={4}
                  className="text-sm"
                  autoFocus
                />

                {getSellerAddress(displayRecords[reviewIndex]).trim() !== '' && (
                  <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-2 rounded">
                    <strong>Saved:</strong> {getSellerAddress(displayRecords[reviewIndex])}
                  </div>
                )}
              </div>

              {/* Document Link */}
              {displayRecords[reviewIndex].pdf_url && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-8"
                  onClick={() => window.open(displayRecords[reviewIndex].pdf_url, '_blank')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Open Document
                </Button>
              )}
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="flex gap-2 pt-2 border-t shrink-0">
              <Button
                onClick={handleDeleteAndNext}
                variant="destructive"
                size="sm"
                className="flex-1 h-9"
              >
                <X className="mr-1 h-4 w-4" />
                Delete
              </Button>
              <Button
                onClick={handleSaveAndNext}
                variant="default"
                size="sm"
                className="flex-1 h-9"
              >
                <Save className="mr-1 h-4 w-4" />
                Save
              </Button>
            </div>

            {/* Keyboard hints */}
            <div className="text-[10px] text-muted-foreground flex gap-3 justify-center shrink-0">
              <span>Ctrl+Enter: Save</span>
              <span>Ctrl+Del: Delete</span>
              <span>Ctrl+←/→: Nav</span>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <NameCleanupWizard
        open={showNameCleanup}
        onClose={() => setShowNameCleanup(false)}
        records={nameCleanupRecords}
        getSellerName={getSellerName}
        getSellerAddress={getSellerAddress}
        onComplete={handleNameCleanupComplete}
        onSaveProgress={handleNameCleanupSaveProgress}
      />
    </>
  );
}
